import React, { lazy, ComponentType } from 'react';

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Error fallback for chunk loading failures
const ChunkErrorFallback = ({ retry }: { retry: () => void }) => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center space-y-4 max-w-md mx-auto p-6">
      <div className="text-6xl">⚠️</div>
      <h1 className="text-2xl font-bold text-foreground">
        Erro ao carregar página
      </h1>
      <p className="text-muted-foreground">
        Falha ao carregar os recursos necessários. Verifique sua conexão e tente novamente.
      </p>
      <button
        onClick={retry}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
      >
        Tentar Novamente
      </button>
    </div>
  </div>
);

/**
 * Retry wrapper for lazy loading with automatic page reload on chunk errors
 * This handles the common "Failed to fetch dynamically imported module" error
 * that occurs after deployments when old chunks are no longer available
 */
const lazyWithRetry = <T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retries = 3,
  interval = 1000
): React.LazyExoticComponent<T> => {
  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      const attemptImport = (attemptsLeft: number) => {
        componentImport()
          .then(resolve)
          .catch((error) => {
            // Check if it's a chunk loading error
            const isChunkError =
              error?.message?.includes('Failed to fetch') ||
              error?.message?.includes('Loading chunk') ||
              error?.message?.includes('ChunkLoadError') ||
              error?.message?.includes('Importing a module script failed');

            if (isChunkError && attemptsLeft > 0) {
              console.warn(`[LazyLoad] Chunk load failed, retrying... (${attemptsLeft} attempts left)`);
              setTimeout(() => attemptImport(attemptsLeft - 1), interval);
            } else if (isChunkError && attemptsLeft === 0) {
              // Last attempt failed - reload the page to get fresh chunks
              console.error('[LazyLoad] All retry attempts failed. Reloading page...');
              window.location.reload();
            } else {
              // Not a chunk error, reject normally
              reject(error);
            }
          });
      };

      attemptImport(retries);
    });
  });
};

// Admin pages - lazy loaded with retry (using named exports)
export const LazyDashboard = lazyWithRetry(() => import('@/pages/admin/Dashboard').then(module => ({ default: module.Dashboard })));
export const LazyAdminProperties = lazyWithRetry(() => import('@/pages/admin/Properties').then(module => ({ default: module.Properties })));
export const LazyPropertyForm = lazyWithRetry(() => import('@/pages/admin/PropertyForm').then(module => ({ default: module.PropertyForm })));
export const LazyPropertyEdit = lazyWithRetry(() => import('@/pages/admin/PropertyEdit').then(module => ({ default: module.PropertyEdit })));
export const LazyCustomization = lazyWithRetry(() => import('@/pages/admin/Customization').then(module => ({ default: module.Customization })));
export const LazyMarketing = lazyWithRetry(() => import('@/pages/admin/Marketing').then(module => ({ default: module.Marketing })));
export const LazyLeads = lazyWithRetry(() => import('@/pages/admin/Leads'));
export const LazyLeadsKanban = lazyWithRetry(() => import('@/pages/admin/LeadsKanban').then(module => ({ default: module.LeadsKanban })));
export const LazyLeadDetail = lazyWithRetry(() => import('@/pages/admin/LeadDetail').then(module => ({ default: module.LeadDetail })));
export const LazyAgendamentos = lazyWithRetry(() => import('@/pages/admin/Agendamentos').then(module => ({ default: module.Agendamentos })));

// Settings pages
export const LazySettings = lazyWithRetry(() => import('@/pages/admin/Settings').then(module => ({ default: module.SettingsPage })));
export const LazyProfileSettings = lazyWithRetry(() => import('@/pages/admin/settings/ProfileSettings').then(module => ({ default: module.ProfileSettings })));
export const LazyDomainSettings = lazyWithRetry(() => import('@/pages/admin/settings/DomainSettings').then(module => ({ default: module.DomainSettings })));
export const LazySubscriptionSettings = lazyWithRetry(() => import('@/pages/admin/settings/SubscriptionSettings').then(module => ({ default: module.SubscriptionSettings })));
export const LazyIntegrationsSettings = lazyWithRetry(() => import('@/components/admin/settings/IntegrationsSettings').then(module => ({ default: module.IntegrationsSettings })));
export const LazyMigration = lazyWithRetry(() => import('@/pages/admin/Migration').then(module => ({ default: module.Migration })));
export const LazyDebugSubdomain = lazyWithRetry(() => import('@/components/DebugSubdomain').then(module => ({ default: module.DebugSubdomain })));
export const LazyAdminLayout = lazyWithRetry(() => import('@/components/AdminLayout').then(module => ({ default: module.AdminLayout })));
export const LazyAdminAccountSettings = lazyWithRetry(() => import('@/pages/admin/settings/AccountSettings').then(module => ({ default: module.AccountSettings })));
export const LazyBusinessSettings = lazyWithRetry(() => import('@/pages/admin/settings/BusinessSettings').then(module => ({ default: module.BusinessSettings })));
export const LazySiteSetupWizard = lazyWithRetry(() => import('@/pages/admin/SiteSetupWizard').then(module => ({ default: module.SiteSetupWizard })));

// New Settings Pages
export const LazyPublicProfile = lazyWithRetry(() => import('@/pages/settings/PublicProfile'));
export const LazyAccountSettings = lazyWithRetry(() => import('@/pages/settings/Account'));
export const LazyTeamSettings = lazyWithRetry(() => import('@/pages/admin/settings/TeamSettings').then(module => ({ default: module.TeamSettings })));
export const LazyReferrals = lazyWithRetry(() => import('@/pages/admin/Referrals'));

// OAuth Callback  
export const LazyOAuthCallback = lazyWithRetry(() => import('@/pages/admin/OAuthCallback').then(module => ({ default: module.OAuthCallback })));

// Financial pages
export const LazyFinanceiro = lazyWithRetry(() => import('@/pages/admin/Financeiro').then(module => ({ default: module.Financeiro })));

// Tools pages
export const LazyGeradorPropostas = lazyWithRetry(() => import('@/pages/admin/GeradorPropostas').then(module => ({ default: module.GeradorPropostas })));
export const LazyPropostas = lazyWithRetry(() => import('@/pages/admin/Propostas').then(module => ({ default: module.Propostas })));

// Marketplace pages - lazy loaded with retry (using named exports)
export const LazyMarketplace = lazyWithRetry(() => import('@/pages/Marketplace').then(module => ({ default: module.Marketplace })));
export const LazyMarketplaceProperties = lazyWithRetry(() => import('@/pages/marketplace/Properties').then(module => ({ default: module.MarketplaceProperties })));
export const LazyPropertyDetail = lazyWithRetry(() => import('@/pages/PropertyDetail').then(module => ({ default: module.PropertyDetail })));
export const LazyAbout = lazyWithRetry(() => import('@/pages/marketplace/About'));
export const LazyFinancing = lazyWithRetry(() => import('@/pages/marketplace/Financing').then(module => ({ default: module.Financing })));

// Auth pages (default export)
export const LazyLogin = lazyWithRetry(() => import('@/pages/Login'));
export const LazySignup = lazyWithRetry(() => import('@/pages/Signup'));
export const LazyJoinTeam = lazyWithRetry(() => import('@/pages/auth/JoinTeam').then(module => ({ default: module.JoinTeam })));

export { PageLoader, ChunkErrorFallback };